import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ShieldCheck } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Label } from '../../components/ui/Label'
import { Logo } from '../../components/ui/Logo'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import toast from 'react-hot-toast'

const mfaChallengeSchema = z.object({
  code: z
    .string()
    .min(6, 'Enter the 6-digit code from your authenticator app')
    .max(6, 'Code must be exactly 6 digits')
    .regex(/^\d{6}$/, 'Code must be 6 digits'),
})

type MfaChallengeFormData = z.infer<typeof mfaChallengeSchema>

export const MfaChallengePage: React.FC = () => {
  const { clearMfaPending } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [submitting, setSubmitting] = useState(false)

  const from = (location.state as any)?.from?.pathname || '/app'

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<MfaChallengeFormData>({
    resolver: zodResolver(mfaChallengeSchema),
  })

  const onSubmit = async (data: MfaChallengeFormData) => {
    setSubmitting(true)
    try {
      const { data: factorsData, error: listError } = await supabase.auth.mfa.listFactors()
      if (listError) throw listError

      const totp = factorsData?.totp?.[0]
      if (!totp) {
        toast.error('No authenticator app found. Please contact support.')
        return
      }

      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: totp.id,
      })
      if (challengeError) throw challengeError

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: totp.id,
        challengeId: challengeData.id,
        code: data.code,
      })

      if (verifyError) {
        setError('code', { message: 'Invalid code. Please try again.' })
        return
      }

      clearMfaPending()
      navigate(from, { replace: true })
    } catch (error: any) {
      toast.error(error.message || 'Verification failed. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white/60 dark:bg-slate-800/60 backdrop-blur-3xl rounded-[28px] shadow-[0_8px_32px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-white/60 dark:border-slate-700/60 ring-1 ring-black/[0.03] dark:ring-white/[0.03] p-8">
        <div className="flex flex-col items-center mb-8">
          <Logo className="h-10 w-auto mb-6" />
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 text-center">
            Two-factor authentication
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 text-center mt-2">
            Enter the 6-digit code from your authenticator app.
          </p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="code">Authentication code</Label>
            <Input
              {...register('code')}
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={6}
              className="text-center text-2xl tracking-[0.5em] font-mono"
              autoFocus
            />
            {errors.code && (
              <p className="text-sm text-destructive">{errors.code.message}</p>
            )}
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={submitting}
          >
            {submitting ? (
              <LoadingSpinner size="sm" variant="icon" className="mr-2" />
            ) : null}
            Verify
          </Button>
        </form>
      </div>
    </div>
  )
}
